import { jsxs, jsx } from 'react/jsx-runtime';
import { forwardRef } from 'react';
import { isBefore, endOfDay, isAfter, startOfDay, isSameDay } from 'date-fns';
import styles from '../Calendar.styles.esm.js';
import { cn } from '../../../utils/cn.esm.js';

const daysOfWeek = [
    { short: "S", long: "Sunday" },
    { short: "M", long: "Monday" },
    { short: "T", long: "Tuesday" },
    { short: "W", long: "Wednesday" },
    { short: "T", long: "Thursday" },
    { short: "F", long: "Friday" },
    { short: "S", long: "Saturday" },
];
// Helper function to generate days for the given month and year
const generateDays = (year, month) => {
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    return Array.from({ length: daysInMonth }, (_, i) => i + 1);
};
const getStartingDayOfMonth = (year, month) => {
    return new Date(year, month, 1).getDay(); // Returns 0 for Sunday, 1 for Monday, etc.
};
const CalendarContent = forwardRef(({ currentDate, calendarDateYear, calendarDateMonth, selectedDate, disableActiveDate, disabledDaysOfTheWeek, disablePastDates, disableFutureDates, disablePastTo, disableFutureFrom, onDayClick, inverse = false, }, ref) => {
    const { content, contentHeader, contentItem, contentRow } = styles({
        inverse,
    });
    const daysInCalendarDate = generateDays(calendarDateYear, calendarDateMonth);
    const startingDay = getStartingDayOfMonth(calendarDateYear, calendarDateMonth);
    const isDisabled = (day) => {
        const dayDate = new Date(calendarDateYear, calendarDateMonth, day);
        if (disabledDaysOfTheWeek &&
            disabledDaysOfTheWeek.includes(dayDate.getDay())) {
            return true; // Disable weekends
        }
        if (disablePastTo &&
            isBefore(dayDate, endOfDay(new Date(disablePastTo)))) {
            return true; // Day is disabled because it is before the allowed range
        }
        if (disableFutureFrom &&
            isAfter(dayDate, startOfDay(new Date(disableFutureFrom)))) {
            return true; // Day is disabled because it is after the allowed range
        }
        if (disablePastDates && isBefore(dayDate, endOfDay(currentDate))) {
            return true; // Day is disabled because it is in the past
        }
        if (disableFutureDates && isAfter(dayDate, startOfDay(currentDate))) {
            return true; // Day is disabled because it is in the future
        }
        return false;
    };
    const checkDateMatchWithCalendar = (day, date) => {
        if (!date)
            return false;
        const dayDate = new Date(calendarDateYear, calendarDateMonth, day);
        return isSameDay(dayDate, date);
    };
    const getDataState = (day) => {
        const matchCurrentDate = checkDateMatchWithCalendar(day, currentDate);
        const matchSelectedDate = checkDateMatchWithCalendar(day, selectedDate);
        if (matchCurrentDate && matchSelectedDate) {
            return "current-selected";
        }
        else if (matchCurrentDate) {
            return "current";
        }
        else if (matchSelectedDate) {
            return "selected";
        }
        return "default";
    };
    const getDataStatus = (day) => {
        if (checkDateMatchWithCalendar(day, selectedDate)) {
            return disableActiveDate ? "active-disabled" : "active";
        }
        return isDisabled(day) ? "disabled" : "default";
    };
    const chunkArray = (array, chunkSize) => {
        const result = [];
        for (let i = 0; i < array.length; i += chunkSize) {
            result.push(array.slice(i, i + chunkSize));
        }
        return result;
    };
    const daysInCalendarDateChunk = chunkArray([
        ...Array.from({ length: startingDay }, () => null),
        ...daysInCalendarDate,
    ], 7);
    return (jsxs("div", { ref: ref, className: content(), "aria-label": `${new Date(calendarDateYear, calendarDateMonth).toLocaleString("default", {
            month: "long",
            year: "numeric",
        })}`, children: [jsx("div", { "aria-hidden": "true", className: cn(contentRow()), children: daysOfWeek.map((day, index) => (jsx("div", { className: cn(contentHeader()), children: day.short }, index))) }), daysInCalendarDateChunk.map((week, weekIndex) => (jsx("div", { className: cn(contentRow()), children: week.map((day, dayIndex) => {
                    if (day === null) {
                        return (jsx("div", { className: cn(contentItem()), "aria-hidden": "true" }, `empty-${weekIndex}-${dayIndex}`));
                    }
                    const isDisable = isDisabled(day);
                    const dataState = getDataState(day);
                    const dataStatus = getDataStatus(day);
                    // Calculate the week number
                    const weekNumber = Math.ceil((day + startingDay) / 7);
                    return (jsx("div", { children: jsx("button", { "data-state": dataState, "data-status": dataStatus, "data-value": day, "data-week": weekNumber, disabled: isDisable, tabIndex: isDisable
                                ? -1
                                : (() => {
                                    const hasSelected = Boolean(selectedDate);
                                    if (!hasSelected) {
                                        // No selected date: make "current" day tabbable
                                        return dataState === "current" ? 0 : -2;
                                    }
                                    // Has selected date: selected/current-selected is tabbable, "current" is not
                                    if (dataState === "selected" ||
                                        dataState === "current-selected")
                                        return 0;
                                    if (dataState === "current")
                                        return -1;
                                    return -2;
                                })(), "aria-label": (() => {
                                const dayOfWeek = daysOfWeek[(day + startingDay - 1) % 7];
                                const selected = dataState === "selected" ||
                                    dataState === "current-selected"
                                    ? "selected"
                                    : "";
                                const month = new Intl.DateTimeFormat("default", {
                                    month: "long",
                                }).format(new Date(calendarDateYear, calendarDateMonth));
                                const today = dataState === "current" ||
                                    dataState === "current-selected"
                                    ? "today"
                                    : "";
                                return `${dayOfWeek.long}, ${month} ${day} ${selected}${today ? `${`, ${today}`}` : ""}`;
                            })(), "aria-disabled": isDisable, className: cn(contentItem()), onClick: () => !isDisable &&
                                dataStatus !== "active-disabled" &&
                                onDayClick(day), children: jsx("span", { "aria-hidden": "true", children: day }) }, day) }, `day-${day}-${weekIndex}-${dayIndex}`));
                }) }, `week-${weekIndex}`)))] }));
});

export { CalendarContent };
